import { useLayoutEffect, useState } from 'react';
import { Link } from 'react-router';
import { ChevronLeft, Pencil } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { CampaignAudienceSection } from '@/components/campaigns/campaign-audience-section';
import { CampaignEditNameDialog } from '@/components/campaigns/campaign-edit-name-dialog';
import {
  CampaignMessageSection,
  type CampaignLinkDestination,
} from '@/components/campaigns/campaign-message-section';
import { CampaignScheduleSection } from '@/components/campaigns/campaign-schedule-section';
import { CampaignTestNotificationDialog } from '@/components/campaigns/campaign-test-notification-dialog';
import { CampaignTrackingSection } from '@/components/campaigns/campaign-tracking-section';
import { LoaderButton } from '@/components/loader-button';
import { PageBreadcrumb } from '@/components/page-breadcrumb';
import { Button } from '@/components/ui/button';
import { WithTooltip } from '@/components/with-tooltip';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import {
  CenteredContent,
  CenteredContentLayout,
  Header,
} from '@/layouts/centered-content.layout';
import { SidebarLayout } from '@/layouts/sidebar.layout';

export interface CampaignCreateFormData {
  name: string;
  title: string;
  body: string;
  link: string;
  linkDestination: CampaignLinkDestination;
  launchDate: Date | undefined;
  launchTime: string;
  timezone: string;
  utmCampaign: string;
  audienceId: string | null;
  suppressRecentNotifications: boolean;
}

export interface CampaignCreateValidationErrors {
  name: string;
  title: string;
  body: string;
  link: string;
  launchDate: string;
  launchTime: string;
  utmCampaign: string;
  audienceId: string;
}

export interface CampaignCreateTouchedFields {
  name: boolean;
  title: boolean;
  body: boolean;
  link: boolean;
  launchDate: boolean;
  launchTime: boolean;
  timezone: boolean;
  utmCampaign: boolean;
  audienceId: boolean;
}

export interface CampaignCreatePresenterProps {
  tenantSlug: string;
  locale: Locales;
  iconUrl?: string | null;
  storeWebsiteDomain?: string | null;
  appScheme: string;
  adminUserId: string;
  formData: CampaignCreateFormData;
  validationErrors: CampaignCreateValidationErrors;
  touchedFields: CampaignCreateTouchedFields;
  isMobile: boolean;
  isTestDialogOpen: boolean;
  onTestDialogOpenChange: (open: boolean) => void;
  isCreating: boolean;
  isSendingTest: boolean;
  isFormValid: boolean;
  isStepValid: (stepIndex: number) => boolean;
  onValidateStep: (stepIndex: number) => boolean;
  canSendTest: boolean;
  onFieldChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onFieldBlur: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onDateChange: (date: Date | undefined) => void;
  onTimezoneChange: (timezone: string) => void;
  onAudienceChange: (audienceId: string | null) => void;
  onAudienceBlur: () => void;
  onSuppressChange: (checked: boolean) => void;
  onNameChange: (name: string) => void;
  onLinkDestinationChange: (value: CampaignLinkDestination) => void;
  isSaveNameDialogOpen: boolean;
  onSaveNameDialogOpenChange: (open: boolean) => void;
  saveNameDialogName: string;
  onSaveNameConfirm: (name: string) => void;
  onSubmit: () => void;
  onOpenTestDialog: () => void;
  onSendTestNotification: () => void;
  audiencesEnabled?: boolean;
}

const SECTION_TRANSITION = {
  duration: 0.3,
  ease: [0.16, 1, 0.3, 1] as const,
};

type CampaignViewMode = 'wizard' | 'full';

export function CampaignCreatePresenter({
  tenantSlug,
  locale,
  iconUrl,
  storeWebsiteDomain,
  appScheme,
  adminUserId,
  formData,
  validationErrors,
  touchedFields,
  isMobile,
  isTestDialogOpen,
  onTestDialogOpenChange,
  isCreating,
  isSendingTest,
  isFormValid,
  isStepValid,
  onValidateStep,
  canSendTest,
  onFieldChange,
  onFieldBlur,
  onDateChange,
  onTimezoneChange,
  onAudienceChange,
  onAudienceBlur,
  onSuppressChange,
  onNameChange,
  onLinkDestinationChange,
  isSaveNameDialogOpen,
  onSaveNameDialogOpenChange,
  saveNameDialogName,
  onSaveNameConfirm,
  onSubmit,
  onOpenTestDialog,
  onSendTestNotification,
  audiencesEnabled = true,
}: CampaignCreatePresenterProps) {
  const { LL } = useI18nContext();
  const [isEditNameOpen, setIsEditNameOpen] = useState(false);
  const [viewMode, setViewMode] = useState<CampaignViewMode>('wizard');
  const [stepIndex, setStepIndex] = useState(0);
  const isWizardMode = viewMode === 'wizard';

  const scrollToTop = () => {
    const main = document.querySelector('main');
    if (!main) return;

    main.scrollTo(0, 0);

    const frameId = requestAnimationFrame(() => {
      main.scrollTo(0, 0);
    });

    return () => cancelAnimationFrame(frameId);
  };

  // SidebarLayout scrolls inside <main>, which ScrollRestoration does not reset.
  useLayoutEffect(() => {
    return scrollToTop();
  }, [stepIndex, viewMode]);

  const sectionEntryClass = (delay: string) =>
    isWizardMode ? undefined : `entry-fade-in-up ${delay}`;

  const messageSection = (
    <CampaignMessageSection
      className={sectionEntryClass('entry-delay-1')}
      title={formData.title}
      body={formData.body}
      link={formData.link}
      linkDestination={formData.linkDestination}
      iconUrl={iconUrl}
      storeWebsiteDomain={storeWebsiteDomain}
      titleError={touchedFields.title ? validationErrors.title : undefined}
      bodyError={touchedFields.body ? validationErrors.body : undefined}
      linkError={touchedFields.link ? validationErrors.link : undefined}
      canSendTest={canSendTest}
      onOpenTestDialog={onOpenTestDialog}
      onLinkDestinationChange={onLinkDestinationChange}
      onChange={onFieldChange}
      onBlur={onFieldBlur}
    />
  );

  const scheduleSection = (
    <CampaignScheduleSection
      className={sectionEntryClass('entry-delay-2')}
      launchDate={formData.launchDate}
      launchTime={formData.launchTime}
      timezone={formData.timezone}
      launchDateError={
        touchedFields.launchDate ? validationErrors.launchDate : undefined
      }
      launchTimeError={
        touchedFields.launchTime ? validationErrors.launchTime : undefined
      }
      onDateChange={onDateChange}
      onChange={onFieldChange}
      onBlur={onFieldBlur}
      onTimezoneChange={onTimezoneChange}
    />
  );

  const audienceSection = (
    <CampaignAudienceSection
      className={sectionEntryClass('entry-delay-3')}
      tenantSlug={tenantSlug}
      audienceId={formData.audienceId}
      suppressRecentNotifications={formData.suppressRecentNotifications}
      locale={locale}
      audienceError={
        touchedFields.audienceId ? validationErrors.audienceId : undefined
      }
      onAudienceChange={onAudienceChange}
      onAudienceBlur={onAudienceBlur}
      onSuppressChange={onSuppressChange}
    />
  );

  const trackingSection = (
    <CampaignTrackingSection
      className={sectionEntryClass('entry-delay-4')}
      utmCampaign={formData.utmCampaign}
      utmCampaignError={
        touchedFields.utmCampaign ? validationErrors.utmCampaign : undefined
      }
      onChange={onFieldChange}
      onBlur={onFieldBlur}
    />
  );

  const sections = [
    messageSection,
    scheduleSection,
    ...(audiencesEnabled ? [audienceSection] : []),
    trackingSection,
  ];

  const isLastStep = stepIndex === sections.length - 1;
  const isContinueAction = isWizardMode && !isLastStep;
  const currentStepValid = isStepValid(stepIndex);

  const handlePrimaryAction = () => {
    if (isContinueAction) {
      if (onValidateStep(stepIndex)) {
        setStepIndex((prev) => prev + 1);
      }
      return;
    }

    onSubmit();
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handlePrimaryAction();
  };

  const primaryActionButton = (
    <WithTooltip
      tooltip={
        isContinueAction
          ? LL.createCampaign.continueTooltip()
          : LL.createCampaign.saveCampaignTooltip()
      }
      disabled={isContinueAction ? currentStepValid : isFormValid}
    >
      <LoaderButton
        type="button"
        onClick={handlePrimaryAction}
        disabled={isContinueAction ? !currentStepValid : !isFormValid}
        isLoading={!isContinueAction && isCreating}
      >
        {isContinueAction
          ? LL.createCampaign.continueButton()
          : LL.createCampaign.saveCampaignButton()}
      </LoaderButton>
    </WithTooltip>
  );

  return (
    <SidebarLayout className="!p-0">
      <CenteredContentLayout>
        <Header
          className="flex w-full items-center justify-between gap-4"
          rightGutter
        >
          <div className="flex min-w-0 flex-col gap-1.5">
            <PageBreadcrumb
              items={[
                {
                  label: LL.campaigns.title(),
                  href: `/${tenantSlug}/campaigns`,
                },
                { label: LL.createCampaign.headerTitle() },
              ]}
            />
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              <Link
                to={`/${tenantSlug}/campaigns`}
                className="shrink-0"
                aria-label={LL.campaigns.title()}
              >
                <ChevronLeft className="size-6" />
              </Link>
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <h1 className="min-w-0 truncate text-lg tracking-tight sm:text-xl">
                  {formData.name.trim() || LL.createCampaign.defaultName()}
                </h1>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  aria-label={LL.createCampaign.editNameAriaLabel()}
                  onClick={() => setIsEditNameOpen(true)}
                >
                  <Pencil className="size-4" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 self-end sm:gap-4">
            <Button
              type="button"
              variant="ghost"
              className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              onClick={() =>
                setViewMode((prev) => (prev === 'wizard' ? 'full' : 'wizard'))
              }
            >
              {isWizardMode
                ? LL.createCampaign.viewAllSections()
                : LL.createCampaign.editStepByStep()}
            </Button>
            {!isWizardMode ? primaryActionButton : null}
          </div>
        </Header>

        <CenteredContent className="pb-8 sm:pb-12">
          <form
            id="campaign-form"
            onSubmit={handleFormSubmit}
            className="flex min-w-0 flex-col gap-8 sm:gap-10"
          >
            {touchedFields.name && validationErrors.name ? (
              <p className="text-sm text-destructive entry-fade-in-up entry-delay-0">
                {validationErrors.name}
              </p>
            ) : null}

            {isWizardMode ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-4">
                  {stepIndex > 0 ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStepIndex((prev) => prev - 1)}
                    >
                      <ChevronLeft className="size-4" />
                      {LL.createCampaign.backButton()}
                    </Button>
                  ) : (
                    <span />
                  )}
                  {primaryActionButton}
                </div>

                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={stepIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={SECTION_TRANSITION}
                    className="min-w-0"
                  >
                    {sections[stepIndex]}
                  </motion.div>
                </AnimatePresence>
              </div>
            ) : (
              sections
            )}
          </form>
        </CenteredContent>

        <CampaignEditNameDialog
          open={isEditNameOpen}
          onOpenChange={setIsEditNameOpen}
          name={formData.name}
          title={LL.createCampaign.editNameDialog.title()}
          onConfirm={onNameChange}
        />

        <CampaignEditNameDialog
          open={isSaveNameDialogOpen}
          onOpenChange={onSaveNameDialogOpenChange}
          name={saveNameDialogName}
          title={LL.createCampaign.saveNameDialog.title()}
          onConfirm={onSaveNameConfirm}
        />

        <CampaignTestNotificationDialog
          open={isTestDialogOpen}
          onOpenChange={onTestDialogOpenChange}
          isMobile={isMobile}
          appScheme={appScheme}
          adminUserId={adminUserId}
          isSending={isSendingTest}
          onSend={onSendTestNotification}
        />
      </CenteredContentLayout>
    </SidebarLayout>
  );
}
